// Authentication Context - Real API Integration
import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { authService, getRoleBasedRoute, getRoleDisplayName, ROLE_PERMISSIONS } from '../services/authService';

const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};

// Re-export helpers for backwards compatibility
export { getRoleBasedRoute, getRoleDisplayName, ROLE_PERMISSIONS };

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  // Check for existing session on mount
  useEffect(() => {
    const initAuth = async () => {
      const token = localStorage.getItem('auth_token');
      
      if (token) {
        // Always verify token by fetching current user from API
        try {
          const currentUser = await authService.getCurrentUser();
          if (currentUser) {
            // Refresh stored user with authoritative API data
            localStorage.setItem('emr_user', JSON.stringify(currentUser));
            setUser(currentUser);
          } else {
            // Token invalid, clear storage
            await authService.logout();
            setUser(null);
          }
        } catch (error) {
          // API unreachable — clear stale data to force re-login
          await authService.logout();
          setUser(null);
        }
      }
      setLoading(false);
    };
    
    initAuth();
  }, []);

  const login = useCallback(async (email, password) => {
    const result = await authService.login(email, password);
    
    if (result.success) {
      setUser(result.user);
      return { 
        success: true, 
        user: result.user,
        redirectTo: result.redirectTo
      };
    }
    
    return { success: false, error: result.error };
  }, []);

  const register = useCallback(async (userData) => {
    const result = await authService.register(userData);
    
    if (result.success) {
      setUser(result.user);
      return { 
        success: true, 
        user: result.user,
        redirectTo: result.redirectTo
      };
    }
    
    return { success: false, error: result.error };
  }, []);

  const ssoLogin = useCallback(async (firebaseIdToken, provider, profile) => {
    const result = await authService.ssoLogin(firebaseIdToken, provider, profile);
    if (result.success) {
      setUser(result.user);
      return { success: true, user: result.user, redirectTo: result.redirectTo };
    }
    if (result.newUser) {
      return { success: false, newUser: true, redirectTo: result.redirectTo };
    }
    return { success: false, error: result.error };
  }, []);

  const logout = useCallback(async () => {
    await authService.logout();
    setUser(null);
  }, []);

  const refreshUser = useCallback(async () => {
    try {
      const currentUser = await authService.getCurrentUser();
      if (currentUser) {
        localStorage.setItem('emr_user', JSON.stringify(currentUser));
        setUser(currentUser);
      }
    } catch { /* ignore */ }
  }, []);

  const hasPermission = useCallback((permission) => {
    return authService.hasPermission(permission, user?.role, user?.roles);
  }, [user?.role, user?.roles]);

  // Helper: check if user has any of the given roles — uses primary `role` as source of truth
  const hasRole = useCallback((role) => {
    const userRole = user?.role?.toLowerCase();
    if (!userRole) return false;
    if (Array.isArray(role)) return role.some(r => userRole === r.toLowerCase());
    return userRole === role.toLowerCase();
  }, [user?.role]);

  const value = {
    user,
    loading,
    login,
    ssoLogin,
    register,
    logout,
    refreshUser,
    hasPermission,
    hasRole,
    isAuthenticated: !!user,
    isAdmin: hasRole(['admin', 'siteadmin', 'tenantadmin', 'superadmin']),
    isSuperAdmin: hasRole('superadmin'),
    isTenantAdmin: hasRole('tenantadmin'),
    isSiteAdmin: hasRole(['siteadmin', 'admin']),
    isDoctor: hasRole('doctor'),
    isNurse: hasRole('nurse'),
    isStaff: hasRole('staff'),
    isPatient: hasRole('patient'),
    isClinicalStaff: hasRole(['admin', 'siteadmin', 'tenantadmin', 'superadmin', 'doctor', 'nurse']),
    tenantId: user?.tenant_id || localStorage.getItem('emr_tenant_id'),
    siteId: user?.site_id || localStorage.getItem('emr_site_id'),
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export default AuthContext;
